import { useMemo } from 'react';

import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { GetDeliveryPlanningHistoryQuery, useGetDeliveryPlanningHistoryQuery } from 'data/__generated__';

import { SectionContainer } from 'components/DetailsView/Section';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageActionsMenu, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';

type HistoryEntry = GetDeliveryPlanningHistoryQuery['deliveryPlanningHistory'][number];

/** `2026-10-07` → `07/10/2026`. La date du PLANNING, à ne pas confondre avec celle de l'écriture. */
const formatPlanningDate = (date: string): string => date.split('-').reverse().join('/');

/** Une ACTION : tout ce qu'un même enregistrement a modifié. */
type HistoryAction = {
    key: string;
    createdAt: number;
    createdByName: string;
    createdBy: string;
    entries: HistoryEntry[];
};

/**
 * Les écritures d'un même clic, ensemble.
 *
 * Un enregistrement du planning touche autant de cases que l'utilisateur en a cochées : les rendre à
 * plat donnerait vingt lignes identiques à la seconde près, sans dire qu'elles sont UN geste.
 */
const groupByAction = (entries: readonly HistoryEntry[]): HistoryAction[] => {
    const actions = new Map<string, HistoryAction>();

    entries.forEach((entry) => {
        // Sans `requestId`, la ligne est sa propre action : son `_id` est une clé nécessairement unique.
        const key = entry.requestId ?? entry._id;
        const action = actions.get(key) ?? {
            key,
            createdAt: entry.createdAt,
            createdBy: entry.createdBy,
            createdByName: entry.createdByName,
            entries: [],
        };

        action.entries.push(entry);
        action.createdAt = Math.max(action.createdAt, entry.createdAt);
        actions.set(key, action);
    });

    return [...actions.values()].sort((a, b) => b.createdAt - a.createdAt);
};

/** Le sujet d'une ligne : le site et le jour de livraison concernés. */
const getSubject = ({ site, siteId, date, deliveryPlanningId }: HistoryEntry): string => {
    // Un planning supprimé ne laisse rien à joindre : on rend l'identifiant plutôt que rien.
    const siteLabel = site?.name ?? siteId ?? deliveryPlanningId;

    return date ? `${siteLabel} · ${formatPlanningDate(date)}` : siteLabel;
};

/**
 * L'historique des modifications du planning de livraison.
 *
 * Une page dédiée et un rendu dédié, pas la section d'audit générique : la question que se pose
 * l'exploitation est « qui a coché ou décoché quoi, et quand ». Le journal brut ne peut pas y
 * répondre — le site et la date sont la clé immuable du document, donc absentes des patches, et une
 * création ne porte aucun patch. Le serveur recompose ; cette page se contente de le dire en français.
 */
export const DeliveryPlanningAudit = () => {
    const { data, loading, error } = useGetDeliveryPlanningHistoryQuery({ fetchPolicy: 'cache-and-network' });

    const actions = useMemo(() => groupByAction(data?.deliveryPlanningHistory ?? []), [data]);

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour au planning', icon: <ArrowLeft />, to: PAGES.deliveryPlanning.url }],
        },
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.deliveryPlanningAudit} />
                </HeaderTitle>
                <PageActionsMenu groups={actionGroups} />
            </Header>
            <Content>
                <ScrollableContent>
                    <SectionContainer title="Historique des modifications" isInitiallyOpen>
                        <div>
                            {loading && !data ? <Loader /> : null}
                            {error ? (
                                <Message $isError>Erreur lors du chargement de l’historique : {error.message}</Message>
                            ) : null}
                            {!loading && !error && !actions.length ? (
                                <Message>Aucune modification enregistrée</Message>
                            ) : null}

                            <ActionList>
                                {actions.map((action) => (
                                    <ActionItem key={action.key}>
                                        <ActionDate>
                                            {getExtendedFormattedDatetime(action.createdAt)} par{' '}
                                            {action.createdByName || action.createdBy || 'Inconnu'}
                                        </ActionDate>
                                        {action.entries.map((entry) => (
                                            <EntryRow key={entry._id}>
                                                <StateTag $isActive={entry.isActive}>
                                                    {entry.isActive == null
                                                        ? 'Indéterminé'
                                                        : entry.isActive
                                                          ? 'Coché'
                                                          : 'Décoché'}
                                                </StateTag>
                                                <Subject>{getSubject(entry)}</Subject>
                                                {/* Une création ne porte aucun patch : son état est
                                                    déduit de ce qui l'entoure, et l'écran doit le dire
                                                    plutôt que de le présenter comme lu. */}
                                                {entry.isReconstructed ? (
                                                    <Reconstructed title="Une création ne consigne pas son contenu : cet état est déduit de l’écriture suivante, ou de l’état actuel du planning.">
                                                        déduit
                                                    </Reconstructed>
                                                ) : null}
                                            </EntryRow>
                                        ))}
                                    </ActionItem>
                                ))}
                            </ActionList>
                        </div>
                    </SectionContainer>
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;

const ActionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ActionItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 6px;
    padding: 8px 12px;
`;

const ActionDate = styled.span`
    font-weight: 600;
    font-size: 14px;
    color: ${({ theme }) => theme.textColor};
`;

const EntryRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
`;

const StateTag = styled.span<{ $isActive?: boolean | null }>`
    flex-shrink: 0;
    min-width: 76px;
    text-align: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.cardBackgroundColor};
    background-color: ${({ theme, $isActive }) =>
        $isActive == null ? theme.lightBorderColor : $isActive ? theme.successColor : theme.darkBorderColor};
`;

const Subject = styled.span`
    font-weight: 500;
`;

const Reconstructed = styled.span`
    font-size: 11px;
    font-style: italic;
    opacity: 0.6;
    cursor: help;
`;

const Message = styled.div<{ $isError?: boolean }>`
    color: ${({ theme, $isError }) => ($isError ? theme.errorColor : theme.textColor)};
    opacity: ${({ $isError }) => ($isError ? 1 : 0.6)};
    font-size: 13px;
    padding: 8px 0;
`;
